<script lang="ts">
import { Vue, Component, Prop } from 'vue-property-decorator';

@Component
export default class TreeNode extends Vue {
  @Prop({ required: true }) node!: any;
  @Prop({ default: 0 }) depth!: number;
  @Prop({ default: 3 }) maxDepth!: number;
  @Prop({ default: 10 }) maxChildren!: number;

  expanded: boolean = this.depth < 1;
  childRefs: HTMLElement[] = [];

  get hasChildren() {
    return this.node.children && this.node.children.length > 0;
  }

  get limitedChildren() {
    if (this.depth >= this.maxDepth) return [];
    return this.node.children.slice(0, this.maxChildren);
  }

  get remainingChildren() {
    if (!this.node.children) return 0;
    return Math.max(0, this.node.children.length - this.maxChildren);
  }

  toggle() {
    if (this.hasChildren) {
      this.expanded = !this.expanded;
      this.$nextTick(this.updateChildRefs);
    }
  }

  setChildRef(el: any) {
    if (el && el.$el) {
      this.childRefs.push(el.$el);
    }
  }

  updateChildRefs() {
    this.childRefs = (this.$refs as any).node?.querySelectorAll('[ref]') || [];
  }

  generateCurve(childEl: HTMLElement) {
    if (!(this.$refs as any).node || !childEl) return '';
    const parentRect = (this.$refs as any).node.getBoundingClientRect();
    const childRect = childEl.getBoundingClientRect();

    const startX = parentRect.left + parentRect.width / 2 - (this.$refs as any).node.offsetLeft;
    const startY = parentRect.bottom - (this.$refs as any).node.offsetTop;
    const endX = childRect.left + childRect.width / 2 - (this.$refs as any).node.offsetLeft;
    const endY = childRect.top - (this.$refs as any).node.offsetTop;

    const midX = (startX + endX) / 2;
    return `M${startX},${startY} Q${midX},${startY} ${endX},${endY}`;
  }
}
</script>

<template>
  <div class="relative" ref="node">
    <div class="flex items-center space-x-2 z-10">
      <button @click="toggle" class="text-xs">
        <span v-if="hasChildren">{{ expanded ? '-' : '+' }}</span>
      </button>
      <div class="border p-1 rounded bg-white shadow text-sm">
        {{ node.name }}
      </div>
    </div>

    <!-- SVG Lignes vers les enfants -->
    <svg
        v-if="expanded && hasChildren"
        class="absolute top-0 left-0 w-full h-full pointer-events-none z-0"
    >
      <path
          v-for="(childRef, index) in childRefs"
          :key="index"
          :d="generateCurve(childRef)"
          stroke="#999"
          fill="transparent"
          stroke-width="1.5"
      />
    </svg>

    <div
        v-if="expanded && hasChildren"
        class="ml-6 mt-2"
    >
      <TreeNode
          v-for="(child, index) in limitedChildren"
          :key="child.id"
          :ref="setChildRef"
          :node="child"
          :depth="depth + 1"
          :maxDepth="maxDepth"
          :maxChildren="maxChildren"
      />
      <div v-if="remainingChildren > 0" class="text-xs text-gray-500 ml-2">
        ...{{ remainingChildren }} more
      </div>
    </div>
  </div>
</template>

<style scoped>
button {
  width: 20px;
  height: 20px;
  line-height: 14px;
  text-align: center;
  border: 1px solid #ccc;
  border-radius: 3px;
  background-color: #f8f8f8;
  cursor: pointer;
}
</style>
